/**
 * SkillDelivery Platform - Risk Assessment Dashboard
 *
 * Copyright © 2025-2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 */

import React, { useState, useEffect } from 'react';
import adminApi from '../../services/adminApi';

const RiskAssessment = () => {
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCalculator, setShowCalculator] = useState(false);
  const [calculatorType, setCalculatorType] = useState('tender');
  const [formData, setFormData] = useState({
    projectName: '',
    projectValue: '',
    projectDuration: 12,
    paymentTerms: 30,
    retentionPercent: 10,
    advancePercent: 0,
    materialPriceVolatility: 50,
    laborAvailability: 70,
    siteAccessRisk: 30,
    regulatoryRisk: 40,
    clientCreditRating: 'B',
    contractType: 'fixed'
  });
  const [results, setResults] = useState(null);

  useEffect(() => {
    loadAssessments();
  }, []);

  const loadAssessments = async () => {
    try {
      const response = await adminApi.get('/risk/assessments');
      setAssessments(response.data.assessments || []);
    } catch (error) {
      console.error('Failed to load assessments:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateRisk = async () => {
    try {
      const endpoint = calculatorType === 'tender'
        ? '/risk/calculate/tender'
        : '/risk/calculate/asymmetric';

      const response = await adminApi.post(endpoint, {
        ...formData,
        projectValue: parseFloat(formData.projectValue) || 0
      });

      setResults(response.data.results);
    } catch (error) {
      console.error('Calculation failed:', error);
      alert('Risk calculation failed: ' + error.message);
    }
  };

  const getRiskColor = (level) => {
    const colors = {
      'MINIMAL': 'bg-green-100 text-green-800',
      'LOW': 'bg-blue-100 text-blue-800',
      'MODERATE': 'bg-yellow-100 text-yellow-800',
      'HIGH': 'bg-orange-100 text-orange-800',
      'CRITICAL': 'bg-red-100 text-red-800'
    };
    return colors[level] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Risk Assessment</h1>
          <p className="text-gray-600">Tender/BoQ Analytics for Civil Construction</p>
        </div>
        <button
          onClick={() => setShowCalculator(!showCalculator)}
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
        >
          {showCalculator ? 'View History' : '+ New Assessment'}
        </button>
      </div>

      {showCalculator ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Calculator Form */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-lg font-semibold mb-4">Risk Calculator</h2>

            {/* Calculator Type Toggle */}
            <div className="flex gap-2 mb-6">
              <button
                onClick={() => setCalculatorType('tender')}
                className={`px-4 py-2 rounded-lg ${calculatorType === 'tender'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 text-gray-700'}`}
              >
                Tender/BoQ Risk
              </button>
              <button
                onClick={() => setCalculatorType('asymmetric')}
                className={`px-4 py-2 rounded-lg ${calculatorType === 'asymmetric'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 text-gray-700'}`}
              >
                Asymmetric Risk
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Project Name</label>
                <input
                  type="text"
                  value={formData.projectName}
                  onChange={(e) => setFormData({...formData, projectName: e.target.value})}
                  className="mt-1 w-full px-3 py-2 border rounded-lg"
                  placeholder="Enter project name"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Project Value (INR)</label>
                  <input
                    type="number"
                    value={formData.projectValue}
                    onChange={(e) => setFormData({...formData, projectValue: e.target.value})}
                    className="mt-1 w-full px-3 py-2 border rounded-lg"
                    placeholder="e.g., 10000000"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Duration (months)</label>
                  <input
                    type="number"
                    value={formData.projectDuration}
                    onChange={(e) => setFormData({...formData, projectDuration: parseInt(e.target.value)})}
                    className="mt-1 w-full px-3 py-2 border rounded-lg"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Payment Terms (days)</label>
                  <input
                    type="number"
                    value={formData.paymentTerms}
                    onChange={(e) => setFormData({...formData, paymentTerms: parseInt(e.target.value)})}
                    className="mt-1 w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Contract Type</label>
                  <select
                    value={formData.contractType}
                    onChange={(e) => setFormData({...formData, contractType: e.target.value})}
                    className="mt-1 w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="fixed">Fixed Price</option>
                    <option value="cost-plus">Cost Plus</option>
                    <option value="hybrid">Hybrid</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Retention %</label>
                  <input
                    type="number"
                    value={formData.retentionPercent}
                    onChange={(e) => setFormData({...formData, retentionPercent: parseInt(e.target.value)})}
                    className="mt-1 w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Advance %</label>
                  <input
                    type="number"
                    value={formData.advancePercent}
                    onChange={(e) => setFormData({...formData, advancePercent: parseInt(e.target.value)})}
                    className="mt-1 w-full px-3 py-2 border rounded-lg"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Client Credit Rating</label>
                <select
                  value={formData.clientCreditRating}
                  onChange={(e) => setFormData({...formData, clientCreditRating: e.target.value})}
                  className="mt-1 w-full px-3 py-2 border rounded-lg"
                >
                  <option value="A">A - Excellent</option>
                  <option value="B">B - Good</option>
                  <option value="C">C - Average</option>
                  <option value="D">D - Poor</option>
                </select>
              </div>

              {/* Risk Sliders */}
              <div className="space-y-3 pt-4 border-t">
                <h3 className="font-medium text-gray-700">Risk Factors (0-100)</h3>

                <div>
                  <label className="flex justify-between text-sm">
                    <span>Material Price Volatility</span>
                    <span>{formData.materialPriceVolatility}%</span>
                  </label>
                  <input
                    type="range"
                    min="0" max="100"
                    value={formData.materialPriceVolatility}
                    onChange={(e) => setFormData({...formData, materialPriceVolatility: parseInt(e.target.value)})}
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="flex justify-between text-sm">
                    <span>Labor Availability</span>
                    <span>{formData.laborAvailability}%</span>
                  </label>
                  <input
                    type="range"
                    min="0" max="100"
                    value={formData.laborAvailability}
                    onChange={(e) => setFormData({...formData, laborAvailability: parseInt(e.target.value)})}
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="flex justify-between text-sm">
                    <span>Site Access Risk</span>
                    <span>{formData.siteAccessRisk}%</span>
                  </label>
                  <input
                    type="range"
                    min="0" max="100"
                    value={formData.siteAccessRisk}
                    onChange={(e) => setFormData({...formData, siteAccessRisk: parseInt(e.target.value)})}
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="flex justify-between text-sm">
                    <span>Regulatory Risk</span>
                    <span>{formData.regulatoryRisk}%</span>
                  </label>
                  <input
                    type="range"
                    min="0" max="100"
                    value={formData.regulatoryRisk}
                    onChange={(e) => setFormData({...formData, regulatoryRisk: parseInt(e.target.value)})}
                    className="w-full"
                  />
                </div>
              </div>

              <button
                onClick={calculateRisk}
                className="w-full py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium"
              >
                Calculate Risk
              </button>
            </div>
          </div>

          {/* Results Panel */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-lg font-semibold mb-4">Results</h2>

            {results ? (
              <div className="space-y-6">
                {/* Overall Score */}
                <div className="text-center p-6 bg-gray-50 rounded-xl">
                  <div className="text-5xl font-bold text-gray-800 mb-2">
                    {results.overallRiskScore}
                  </div>
                  <div className={`inline-block px-4 py-2 rounded-full text-sm font-medium ${getRiskColor(results.riskLevel)}`}>
                    {results.riskLevel} RISK
                  </div>
                </div>

                {/* Risk Breakdown */}
                {results.breakdown && (
                  <div>
                    <h3 className="font-medium mb-3">Risk Breakdown</h3>
                    <div className="space-y-2">
                      {Object.entries(results.breakdown).map(([key, data]) => (
                        <div key={key} className="flex items-center gap-3">
                          <span className="w-24 text-sm capitalize">{key}</span>
                          <div className="flex-1 bg-gray-200 rounded-full h-3">
                            <div
                              className="bg-indigo-600 rounded-full h-3"
                              style={{ width: `${data.score}%` }}
                            />
                          </div>
                          <span className="w-12 text-sm text-right">{data.score}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Financial Impact */}
                {results.financialImpact && (
                  <div className="p-4 bg-yellow-50 rounded-lg">
                    <h3 className="font-medium mb-2">Financial Impact</h3>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>Cost of Capital:</div>
                      <div className="font-medium">INR {results.financialImpact.costOfCapital?.toLocaleString()}</div>
                      <div>As % of Project:</div>
                      <div className="font-medium">{results.financialImpact.costOfCapitalPercent}%</div>
                      <div>Risk-Adjusted Value:</div>
                      <div className="font-medium">INR {results.financialImpact.riskAdjustedValue?.toLocaleString()}</div>
                      <div>Potential Loss:</div>
                      <div className="font-medium text-red-600">INR {results.financialImpact.potentialLoss?.toLocaleString()}</div>
                    </div>
                  </div>
                )}

                {/* Recommendations */}
                {results.recommendations?.length > 0 && (
                  <div>
                    <h3 className="font-medium mb-2">Recommendations</h3>
                    <div className="space-y-2">
                      {results.recommendations.map((rec, idx) => (
                        <div key={idx} className="p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              rec.priority === 'CRITICAL' ? 'bg-red-100 text-red-800' :
                              rec.priority === 'HIGH' ? 'bg-orange-100 text-orange-800' :
                              'bg-yellow-100 text-yellow-800'
                            }`}>
                              {rec.priority}
                            </span>
                            <span className="font-medium text-sm">{rec.area}</span>
                          </div>
                          <p className="text-sm text-gray-600">{rec.action}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <div className="text-4xl mb-3">📊</div>
                <p>Enter project details and click Calculate Risk</p>
              </div>
            )}
          </div>
        </div>
      ) : (
        /* Assessment History Table */
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Project</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Value</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Risk Score</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                    Loading assessments...
                  </td>
                </tr>
              ) : assessments.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                    No assessments yet. Create your first risk assessment.
                  </td>
                </tr>
              ) : (
                assessments.map((assessment) => (
                  <tr key={assessment.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium">{assessment.project_name}</td>
                    <td className="px-6 py-4">INR {assessment.project_value?.toLocaleString()}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded-full text-xs ${getRiskColor(assessment.risk_level)}`}>
                        {assessment.risk_score || 'N/A'}
                      </span>
                    </td>
                    <td className="px-6 py-4">{assessment.status}</td>
                    <td className="px-6 py-4 text-gray-500">
                      {new Date(assessment.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default RiskAssessment;
